import React, { useState } from 'react';
import styled from '@emotion/styled';
import { useSupportUserDashboard } from './useSupportUserDashboard';
import { useModal } from 'ui/components/screens/modals/useModal';
import { InviteModal } from './InviteModal';
import { InvitationTable } from 'ui/components/supporter/features/InvitationTable/InvitationTable';
import { RegisteredCompanyTable } from 'ui/components/supporter/features/RegisteredCompanyTable/RegisteredCompanyTable';
import { SnackBar } from 'ui/components/elements/SnackBar/SnackBar';
import { mqUser } from 'utils/responsive';
import { useNavigate } from 'react-router-dom';
import { PageTitle } from 'ui/components/elements/PageTitle/PageTitle';

const BgWrapper = styled.div`
    background-color: var(--supporter-bg);
    height: 100%;
    width: 100%;
    padding: 40px 20px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    overflow-y: auto;
    min-height: calc(100vh - var(--height-pc-header));

    ${mqUser('SP')} {
        padding: 20px 15px;
    }
`;

const Container = styled.div`
    width: 100%;
    max-width: 1200px;
    background-color: var(--white);
    border-radius: 8px;
    padding: 30px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

    ${mqUser('SP')} {
        padding: 20px 15px;
    }
`;

const Header = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 30px;
`;

const Title = styled.h1`
    font-size: var(--font-x-large);
    font-weight: var(--font-bold);
    color: var(--main-font-color);
    margin: 0;
`;

const InviteButton = styled.button`
    background-color: var(--main-color-blue);
    color: var(--white);
    border: none;
    border-radius: 6px;
    padding: 12px 24px;
    font-size: var(--font-medium);
    font-weight: var(--font-bold);
    cursor: pointer;
    transition: background-color 0.2s;

    &:hover {
        background-color: #0056b3;
    }

    ${mqUser('SP')} {
        padding: 10px 16px;
        font-size: var(--font-small);
    }
`;

const TabContainer = styled.div`
    display: flex;
    gap: 0;
    border-bottom: 2px solid #e0e0e0;
    margin-bottom: 20px;
`;

const Tab = styled.button<{ active: boolean }>`
    background: none;
    border: none;
    padding: 12px 24px;
    font-size: var(--font-medium);
    font-weight: var(--font-bold);
    cursor: pointer;
    color: ${(props) => (props.active ? 'var(--main-color-blue)' : '#666')};
    border-bottom: 2px solid ${(props) => (props.active ? 'var(--main-color-blue)' : 'transparent')};
    transition: all 0.2s ease;

    &:hover {
        color: var(--main-color-blue);
        background-color: #f8f9fd;
    }

    ${mqUser('SP')} {
        padding: 10px 16px;
        font-size: var(--font-small);
    }
`;

type TabType = 'invited' | 'registered';

export const SupportUserDashboard: React.FC = () => {
    const {
        loading,
        showSnackbar,
        setShowSnackbar,
        snackbarMessage,
        invitedEmails,
        registeredCompanies,
        inviteUser,
        importUsersCsv,
        findChatRoomId,
    } = useSupportUserDashboard();
    const { openModal, closeModal, createModal } = useModal();
    const [activeTab, setActiveTab] = useState<TabType>('registered');
    const navigate = useNavigate();

    const modalContent = (
        <InviteModal
            onClose={closeModal}
            onInviteUser={inviteUser}
            onImportUsersCsv={importUsersCsv}
            loading={loading}
        />
    );

    return (
        <BgWrapper>
            <SnackBar message={snackbarMessage} open={showSnackbar} setOpen={setShowSnackbar} />
            {createModal(modalContent, {
                keyboard: true,
                maskClosable: true,
                onClose: closeModal,
                scrollable: true,
            })}
            <Container>
                <Header>
                    <Title>企業管理</Title>
                    <InviteButton onClick={openModal}>招待する</InviteButton>
                </Header>

                <TabContainer>
                    <Tab active={activeTab === 'registered'} onClick={() => setActiveTab('registered')}>
                        登録済み企業一覧
                    </Tab>
                    <Tab active={activeTab === 'invited'} onClick={() => setActiveTab('invited')}>
                        招待中のメールアドレス
                    </Tab>
                </TabContainer>

                {activeTab === 'invited' && <InvitationTable invitedEmails={invitedEmails} />}

                {activeTab === 'registered' && (
                    <RegisteredCompanyTable
                        registeredCompanies={registeredCompanies}
                        onEdit={(companyId, type) => {
                            if (type === 'seller') {
                                navigate(`/supporter/company/edit/${type}/${companyId}`);
                            } else {
                                navigate(`/supporter/buyer/edit/${type}/${companyId}`);
                            }
                        }}
                        onMessage={async (companyId, type) => {
                            const chatRoomId = await findChatRoomId(companyId, type);
                            if (chatRoomId) {
                                navigate(`/supporter/message/${chatRoomId}`);
                            } else {
                                navigate('/supporter/message/');
                            }
                        }}
                    />
                )}
            </Container>
        </BgWrapper>
    );
};
